import type { Industry } from './account.js';
import type { IsoTimestamp } from './common.js';
import type { PortalAssetLifecycle } from './portalAsset.js';
import type { PortalAudienceRule, PortalAudienceRuleReach } from './portalLibrary.js';

// CLIENT PORTAL — INSPIRATION (Run I, 2026-10-16). Kyck staff upload photo and video
// ideas with a short "how to"; clients view them. View only for clients.
//
// ⚠️⚠️ THE ENTRY IS THE TILE; THE PORTAL ASSET IS THE FILE (the portalLibrary
// posture). The entry holds the words, audience and status. The media is an ordinary
// portal asset under ONE reserved account id, so upload sessions, the worker, HEIC
// decode, video transcode, renditions, CDN signing, soft delete and the purge sweep
// are all reused — and the worker is untouched.

/**
 * The reserved account every inspiration asset lives under. ⚠️ It is NOT an account:
 * no `accounts` doc exists for it, so every roster-driven read (scopes, reach, staff
 * rosters, reports) drops it naturally, and the places that list assets WITHOUT a
 * roster (staffList) drop it explicitly. It matches paths.ts's ID_SEGMENT
 * (letters, digits, `_`, `-`); a test pins that.
 */
export const INSPIRATION_LIBRARY_ACCOUNT_ID = '_kyck_inspiration';

export const INSPIRATION_MEDIA_TYPES = ['image', 'video'] as const;
export type InspirationMediaType = (typeof INSPIRATION_MEDIA_TYPES)[number];

export const INSPIRATION_USAGES = ['advertising', 'organic', 'both'] as const;
export type InspirationUsage = (typeof INSPIRATION_USAGES)[number];

export const INSPIRATION_USAGE_LABELS: Record<InspirationUsage, string> = {
  advertising: 'Advertising',
  organic: 'Organic posting',
  both: 'Ads and organic',
};

/**
 * Which business the idea is ADAPTED for. 'none' = the idea is shown as it is (an on-brand reference).
 * Anything else makes the AI describe the trend and then say how that kind of business does its own version.
 */
export type InspirationAdaptFor = Industry | 'none';

export const INSPIRATION_STATUSES = ['draft', 'published'] as const;
export type InspirationStatus = (typeof INSPIRATION_STATUSES)[number];

/** The text fields the model may write. ⚠️ `tags` and `usage` carry provenance too. */
export const INSPIRATION_AI_FIELDS = ['title', 'trendLine', 'suggestedLines', 'description', 'howTo', 'tags', 'usage'] as const;
export type InspirationAiField = (typeof INSPIRATION_AI_FIELDS)[number];

export type InspirationAiProvenance = {
  /** The model AS THE API REPORTED IT. */
  model: string;
  at: IsoTimestamp;
  /** Fields the model wrote that no person has since edited. Server-decided, never the request's. */
  fields: InspirationAiField[];
  /** How many frames (or 1 for an image) the model was actually shown. */
  framesSeen: number;
};

export type PortalInspirationEntry = {
  id: string;
  mediaType: InspirationMediaType;
  /** The portal asset (under INSPIRATION_LIBRARY_ACCOUNT_ID). ⚠️ Never sent to a client. */
  assetId: string;
  title: string;
  /** The ORIGINAL on-screen or spoken line of the trend (was `hook`; old entries read as this). */
  trendLine: string | null;
  /** 1-3 replacement lines for the target business (empty when adaptFor is 'none'). */
  suggestedLines: string[];
  adaptFor: InspirationAdaptFor;
  /** Plain text with `**payoff**` markup. Rendered safely — see parseInspirationBold. */
  description: string;
  howTo: string;
  /** null until chosen; REQUIRED to publish. */
  usage: InspirationUsage | null;
  tags: string[];
  /** Exactly the HTML uploader's rules and union semantics. An empty list matches NOBODY. */
  audience: PortalAudienceRule[];
  status: InspirationStatus;
  visibleFrom: IsoTimestamp;
  /** null = no end date. */
  visibleUntil: IsoTimestamp | null;
  ai: InspirationAiProvenance | null;
  createdAt: IsoTimestamp;
  createdBy: string;
  updatedAt: IsoTimestamp;
  updatedBy: string;
  publishedAt: IsoTimestamp | null;
  publishedBy: string | null;
};

/**
 * What a CLIENT gets. ⚠️ NO audience, NO assetId, NO createdBy, NO storage path: an
 * `accountSubtree` rule names another client's account, and the asset id reveals the
 * reserved account in a rendition path. Built field by field (toInspirationCard).
 */
export type PortalInspirationCard = {
  id: string;
  mediaType: InspirationMediaType;
  title: string;
  trendLine: string | null;
  suggestedLines: string[];
  description: string;
  howTo: string;
  usage: InspirationUsage;
  tags: string[];
  /** Signed CDN URLs; null where the rendition is not available. */
  thumbUrl: string | null;
  /** Image: the large rendition. Video: null. */
  imageUrl: string | null;
  posterUrl: string | null;
  videoUrl: string | null;
  publishedAt: IsoTimestamp;
};

/** "This will appear for N accounts across M brands" — the same line the HTML uploader shows. */
export type InspirationReach = {
  accountCount: number;
  brandCount: number;
  /** A subset of `accountCount`: accounts some portal user can see TODAY. */
  accountsWithPortalAccess: number;
  perRule: PortalAudienceRuleReach[];
};

/** What staff see of the file behind an entry. ⚠️ Staff only — never sent to a client. */
export type StaffInspirationAsset = {
  lifecycle: PortalAssetLifecycle;
  failureReason: string | null;
  originalFilename: string;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  thumbUrl: string | null;
  previewUrl: string | null;
  posterUrl: string | null;
  videoUrl: string | null;
  deleted: boolean;
};

export type StaffInspirationRow = {
  entry: PortalInspirationEntry;
  asset: StaffInspirationAsset | null;
  reach: InspirationReach;
};
